import React, { useState, useEffect, useMemo } from "react";
import Table from "react-bootstrap/Table";
import EditIcon from "../../../assets/images/edit.png";
import DeleteIcon from "../../../assets/images/delete.png";
import SearchBar from "../../common/components/SearchBar";
import { Button } from "../../common/Button";
import { Link, useNavigate } from "react-router-dom";
import { Skeleton, TableCell, Tooltip, Switch } from "@mui/material";
import CustomPagination from "../../common/components/Pagination/CustomPagination";
import { useConfirmDialog } from "../../../hooks/useConfirmDialog";
import { failed, success } from "../../common/Toastify";
import { Spinner } from "react-bootstrap";
import EditQuestionnaire from "./EditQuestionnaire";
import AssignQuestionnaire from "./AssignQuestionnaire";
import { deleteQuestionnaire, editQuestionnaire } from "../../api/Questionnaire";
import moment from "moment";
import { useSelector } from "react-redux";
import ViewIcon from "../../../assets/images/view.png";
import { ArrowDropDown, ArrowDropUp, CopyAll } from "@mui/icons-material";
import CreateCopyOfDefaultQuestionnaire from "./CreateCopyOfDefaultQuestionnaire";
import { questionnaireForOptions } from "./Constant";
import SearchSelect from "../../common/textfield/SearchSelect";
import { useFormik } from "formik";
import { useOrgQuery } from "../../../hooks/ReactQueryHooks/useOrgQuery";
import { useQuestionnaireSortingQuery } from "../../../hooks/ReactQueryHooks/useQuestionnaireSortingQuery";
import { getOrganization } from "../../api/Organization";

const isChildOrSameOrg = (itemOrgId, currentOrgId) => {
  if (!itemOrgId || !currentOrgId) return false;
  return itemOrgId === currentOrgId || itemOrgId.startsWith(`${currentOrgId}.`);
};

const getAncestors = (orgId) => {
  if (!orgId || !orgId.includes(".")) return [];
  const parts = orgId.split(".");
  const out = [];
  for (let i = 1; i < parts.length; i++) {
    out.push(parts.slice(0, i).join("."));
  }
  return out;
};

function QuestionnaireList() {
  const { getConfirmation } = useConfirmDialog();
  const userType = useSelector((s) => s?.auth?.user?.["cognito:groups"]);
  const admin = userType?.includes("Super-Admin");
  const adminOrgs = useSelector((s) => s?.auth?.user?.organizations) || [];
  const homeOrgId = adminOrgs?.[0]?.id || "";
  const isNonAdmin = !admin;

  const [orgID, setOrgID] = useState("All"); // "All" | "ancestors" | <orgId>
  const [modalShow, setModalShow] = useState(false);
  const [createCopy, setCreateCopy] = useState(false);
  const [assignData, setAssignData] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [filterData, setFilterData] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState("AscName");
  const [organizations, setOrganizations] = useState([]);
  const [organizationData, setOrganizationData] = useState(null);

  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const totalRows = filterData.length;

  const queryOrgId =
    orgID === "All" ? homeOrgId : orgID === "ancestors" ? homeOrgId : orgID;

  const queryGetAllAuthorized =
    orgID === "All" ? true : orgID === "ancestors" ? true : false;

  const { isLoading, refetch, isFetching, data: programList } =
    useQuestionnaireSortingQuery({
      orgID: queryOrgId,
      getAllAuthorized: queryGetAllAuthorized,
      isDefault: true,
    });
  const baseList = useMemo(() => {
    const deletedId = localStorage.getItem("lastDeletedQuestionnaireId");
    const list = programList ?? [];
    const out = deletedId ? list.filter((item) => item.id !== deletedId) : list;
    if (deletedId) localStorage.removeItem("lastDeletedQuestionnaireId");
    return out;
  }, [programList]);

  const ancestorIds = useMemo(() => getAncestors(homeOrgId), [homeOrgId]);
  const shallowEqualArray = (a, b) => {
    if (a === b) return true;
    if (!a || !b || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i]?.id !== b[i]?.id) return false;
    }
    return true;
  };

  const listForUI = useMemo(() => {
    if (isNonAdmin && orgID === "ancestors") {
      return baseList.filter((item) => ancestorIds.includes(item?.orgId));
    }
    return baseList;
  }, [baseList, isNonAdmin, orgID, ancestorIds]);

  useEffect(() => {
    if (!shallowEqualArray(filterData, listForUI)) {
      setFilterData(listForUI || []);
      setPage(0);
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listForUI]);

  useEffect(() => {
    if (queryOrgId) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgID, queryGetAllAuthorized]);

  const searchKey = useMemo(
    () => `${orgID}|${queryGetAllAuthorized}|${homeOrgId}`,
    [orgID, queryGetAllAuthorized, homeOrgId]
  );

  const handleFilterData = (data) => {
    setFilterData(data || []);
    setPage(0);
  };

  const handleShow = (data, isReadOnly) => {
    setModalShow(modalShow ? false : data);
    setReadOnly(isReadOnly);
  };

  const handleDelete = async (id, entryName, purposeData) => {
    const confirmed = await getConfirmation({
      title: "Attention!",
      entryName,
      actionBtnTitle: "Delete",
      message: `Delete "${entryName}"? This will retire the questionnaire.`,
    });
    if (!confirmed) return;

    try {
      setDeleting(id);
      const payload = { status: "retired", purpose: purposeData, type: "questionnaire" };
      await deleteQuestionnaire(id, payload);
      setFilterData((prev) => prev.filter((d) => d.id !== id));
      success("Questionnaire Deleted!");
      refetch();
    } catch (res) {
      failed(res?.response?.data?.message || res?.response?.data?.error || res.message);
    } finally {
      setDeleting(false);
    }
  };

  const sortFilterData = (sort_by) => {
    setSortBy(sort_by);
    switch (sort_by) {
      case "AscName":
        setFilterData((prev) =>
          [...prev].sort((a, b) => (a.title > b.title ? 1 : b.title > a.title ? -1 : 0))
        );
        break;
      case "DscName":
        setFilterData((prev) =>
          [...prev].sort((a, b) => (a.title < b.title ? 1 : b.title < a.title ? -1 : 0))
        );
        break;
      default:
        break;
    }
  };

  const getCategoryTypeName = (categoryType) => {
    const category = questionnaireForOptions.find((o) => o.value === categoryType);
    return category ? category.name : categoryType;
  };

  const handleStatusToggle = async (id, currentStatus, purposeData) => {
    const confirmed = await getConfirmation({
      title: "Attention!",
      actionBtnTitle: "Change Status",
      message: `Are you sure you want to change the status to ${
        currentStatus === "active" ? "Inactive" : "Active"
      }?`,
    });
    if (!confirmed) return;

    try {
      const newStatus =
        currentStatus === "active"
          ? { status: "draft", purpose: purposeData, type: "questionnaire" }
          : { status: "active", purpose: purposeData, type: "questionnaire" };

      const updatedData = await editQuestionnaire(id, newStatus);
      setFilterData((prev) =>
        prev.map((item) => (item.id === id ? { ...item, status: updatedData.status } : item))
      );
      success("Status updated successfully!");
      refetch();
    } catch (error) {
      failed(error?.response?.data?.message || error?.response?.data?.error || error.message);
    }
  };

  const formik = useFormik({
    initialValues: { org: "All", type: "All" },
  });

  useOrgQuery({
    onSuccess: (data) => {
      setOrganizations((data || []).map((org) => ({ name: org?.name, value: org?.id })));
    },
    refetchOnWindowFocus: false,
  });

  const orgOptions = useMemo(() => {
    const opts = [
      { name: "All", value: "All" },
      // ...(isNonAdmin ? [{ name: "Ancestors", value: "ancestors" }] : []),
      ...organizations,
    ];
    const seen = new Set();
    return opts.filter((o) => {
      if (!o?.value) return false;
      if (seen.has(o.value)) return false;
      seen.add(o.value);
      return true;
    });
  }, [isNonAdmin, organizations]);

  const orgSelectOptions = useMemo(() => orgOptions.map((o) => ({ label: o.name, value: o.value })), [orgOptions]);

  const handleOrgChange = (selected) => {
    const value = selected?.value || "";
    formik.setFieldValue("org", value);
    setOrgID(value);
    setPage(0);
  };

  const handleChildSubmit = () => setAssignData(true);

  useEffect(() => {
    const fetchOrganizationData = async () => {
      try {
        if (!orgID || orgID === "All" || orgID === "ancestors") {
          setOrganizationData(null);
          return;
        }
        const response = await getOrganization(orgID);
        setOrganizationData(response.data);
      } catch (error) {
        console.error("Error fetching organization data:", error);
        setOrganizationData(null);
      }
    };
    fetchOrganizationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, refetch, isFetching, assignData, orgID]);

  let assignArr = [];
  if (organizationData?.assignedQuestionnaire) {
    assignArr = [
      ...(organizationData?.assignedQuestionnaire?.["video-visit"] || []),
      ...(organizationData?.assignedQuestionnaire?.["getcare-waitingroom"] || []),
      ...(organizationData?.assignedQuestionnaire?.["telephone-intake"] || []),
      ...(organizationData?.assignedQuestionnaire?.["contact-center"] || []),
    ].flat();
  }

  const startIndex = page * rowsPerPage;

  return (
    <React.Fragment>
      <section className="common-listing">
        {assignData ? (
          <AssignQuestionnaire
            modalData={assignData}
            handleHide={() => setAssignData(false)}
            refreshTable={refetch}
          />
        ) : null}
        {modalShow ? (
          <EditQuestionnaire
            modalShow={modalShow}
            handleShow={handleShow}
            refreshTable={refetch}
            isReadOnly={readOnly}
          />
        ) : null}
        {createCopy ? (
          <CreateCopyOfDefaultQuestionnaire
            modalData={createCopy}
            handleHide={() => setCreateCopy(false)}
            refreshTable={refetch}
          />
        ) : null}

        <div className="heading-wrap h-change">
          <SearchBar
            key={searchKey}                
            page={page}
            setPage={setPage}
            list={listForUI}
            filterData={handleFilterData}
          />

          <div className="right-wrap ml-auto" style={{ paddingTop: 25 }}>
            <form className="common-form border-fields dropdown-filter-rw d-flex align-items-center">
              <SearchSelect
                keyField="org"
                label=""
                formik={formik}
                placeholder="Select Organization"
                options={orgSelectOptions}
                value={orgSelectOptions.find((o) => o.value === formik.values.org) || null}
                onChange={handleOrgChange}
                isDisabled={isLoading}
              />
            </form>
          </div>

          <div className="right-wrap ml-auto m-0">
              <Button
                onClick={() => setAssignData(true)}
                className="custom-btn"
                title="Assign Questionnaire"
                style={{ width: "auto" }}
              >
                Assign
              </Button>
            <Button
              onClick={() => navigate("/app/create-questionnaire")}
              className="custom-btn"
              title="Create Questionnaire"
              style={{ width: "auto" }}
            >
              Create
            </Button>
          </div>
        </div>

        <div className="table-wrap">
          <Table responsive>
            <thead>
              <tr>
                <th>S.No</th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => sortFilterData(sortBy === "AscName" ? "DscName" : "AscName")}
                >
                  Questionnaire Name
                  {sortBy === "AscName" ? <span><ArrowDropUp /></span> : <span><ArrowDropDown /></span>}
                </th>
                <th>Workflow</th>
                <th>Organization</th>
                <th>Status</th>
                <th style={{ textAlign: "center" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading || isFetching ? (
                [1, 2, 3, 4, 5, 6, 7]?.map((val) => (
                  <tr key={val}>
                    <td><Skeleton animation="wave" /></td>
                    <td><Skeleton animation="wave" /></td>
                    <td><Skeleton animation="wave" /></td>
                    <td><Skeleton animation="wave" /></td>
                    <td><Skeleton animation="wave" /></td>
                    <td><Skeleton animation="wave" /></td>
                  </tr>
                ))
              ) : filterData?.length > 0 ? (
                filterData.slice(startIndex, startIndex + rowsPerPage).map((data, index) => {
                  const serialNumber = startIndex + index + 1;

                  
                  const isSpecificOrg = orgID && orgID !== "All" && orgID !== "ancestors";
                  const isAssigned = isSpecificOrg && assignArr?.includes?.(data?.id);

                 
                  const isSuperAdmin = admin;
                  const viewerOrgId = homeOrgId;
                  const canEdit =
                    isSuperAdmin || (!data?.isDefault && isChildOrSameOrg(data?.orgId, viewerOrgId));

                  return (
                    <tr key={data?.id}>
                      <td className="name-text">{isAssigned ? <b>{serialNumber}</b> : serialNumber}</td>
                      <td>{isAssigned ? <b>{data?.title}</b> : data?.title}</td>
                      <td>
                        {isAssigned ? (
                          <b>{getCategoryTypeName(data?.purpose)}</b>
                        ) : (
                          getCategoryTypeName(data?.purpose)
                        )}
                      </td>
                      <td>{organizations.find(org => org.value == data?.orgId)?.name || "Default"}</td>
                      {/* STATUS (gated) */}
                      <td>
                        <Switch
                          disabled={!canEdit}
                          name="status"
                          checked={data?.status === "active"}
                          onChange={() => handleStatusToggle(data?.id, data?.status, data?.purpose)}
                        />
                      </td>

                      {/* ACTIONS */}
                      <td className="action-wrap-container">
                        <div className="actions-styles action-wrap">
                          {/* Copy default when view-only */}
                          {!canEdit && data?.isDefault && (
                            <Tooltip title={"Copy Default Questionnaire"}>
                              <Link
                                to=""
                                variant="primary"
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate("/app/edit-questionnaire", {
                                    state: { initialState: data, isReadOnly: false, actionType: "CREATE", createCopy: true },
                                  });
                                }}
                                className="delete-btn"
                              >
                                <CopyAll style={{ color: "#fff", width: "60%" }} />
                              </Link>
                            </Tooltip>
                          )}

                          {canEdit ? (
                            <>
                              <Tooltip title={"Edit"}>
                                <Link
                                  to=""
                                  variant="primary"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    navigate("/app/edit-questionnaire", {
                                      state: { initialState: data, isReadOnly: false, actionType: "UPDATE" },
                                    });
                                  }}
                                  className="success-btn"
                                >
                                  <img src={EditIcon} alt="Edit" />
                                </Link>
                              </Tooltip>

                              {/* <Tooltip title={"Delete"}>
                                <Link
                                  to=""
                                  variant="primary"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    if (!deleting) handleDelete(data?.id, data?.title, data?.purpose);
                                  }}
                                  className="delete-btn"
                                >
                                  {deleting === data?.id ? (
                                    <Spinner
                                      size="lg"
                                      className="text-current"
                                      style={{ color: "white", background: "transparent", margin: 0 }}
                                    />
                                  ) : (
                                    <img src={DeleteIcon} alt="Delete" />
                                  )}
                                </Link>
                              </Tooltip> */}
                            </>
                          ) : (
                            <Tooltip title={"View"}>
                              <Link
                                to=""
                                variant="primary"
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate("/app/edit-questionnaire", {
                                    state: { initialState: data, isReadOnly: true, actionType: "VIEW" },
                                  });
                                }}
                                className="success-btn"
                              >
                                <img src={ViewIcon} alt="View" />
                              </Link>
                            </Tooltip>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <TableCell colSpan="5" style={{ textAlign: "center" }}>
                    No Data Available
                  </TableCell>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {filterData?.length > 0 ? (
          <CustomPagination
            tableData={filterData}
            totalRows={totalRows}
            rowsPerPage={rowsPerPage}
            page={page}
            handlePage={(pageNo) => setPage(pageNo)}
          />
        ) : null}
      </section>
    </React.Fragment>
  );
}

export default QuestionnaireList;
